import PocketBase from 'pocketbase'
import { PersistenceProvider, QueryBuilder } from './provider.interface.js'
import { config } from '../config.js'

/**
 * PocketBase 持久化提供商实现
 */
export class PocketBaseProvider extends PersistenceProvider {
  constructor() {
    super()

    const url = config.POCKETBASE_URL || 'http://localhost:8090'

    this.client = new PocketBase(url)
    this.authStateCallbacks = []
    this.authRefreshPromise = null

    // 启用会话持久化（localStorage）
    // PocketBase authStore 使用 AsyncAuthStore（默认）在浏览器中
    // 确保它被正确初始化以使用 localStorage
    if (typeof window !== 'undefined') {
      console.log('PocketBase 初始化 - authStore 将使用 localStorage')
      // authStore 会自动使用 localStorage
      // 检查是否已有现存的认证数据
      const existingAuth = localStorage.getItem('pocketbase_auth')
      console.log('localStorage pocketbase_auth 存在:', !!existingAuth)
      if (existingAuth) {
        console.log('localStorage pocketbase_auth 内容:', existingAuth)
      }
    }

    this.initializeAuthStateListener()
    // 延迟恢复认证状态，确保 authStore 已完全初始化
    this.authReadyPromise = new Promise(resolve => {
      setTimeout(() => {
        this.restoreAuthState().finally(resolve)
      }, 0)
    })
  }

  async ensureAuthReady() {
    if (this.authReadyPromise) {
      await this.authReadyPromise
    }
  }

  async tryRefreshAuth() {
    if (!this.client?.authStore?.token) {
      return false
    }

    if (this.authRefreshPromise) {
      return this.authRefreshPromise
    }

    this.authRefreshPromise = (async () => {
      try {
        console.log('检测到 PocketBase token，尝试刷新会话以同步状态...')
        await this.client.collection('users').authRefresh()
        console.log('PocketBase 会话刷新成功')
        return true
      } catch (error) {
        console.error('PocketBase 会话刷新失败:', error)
        return false
      } finally {
        this.authRefreshPromise = null
      }
    })()

    return this.authRefreshPromise
  }

  async restoreAuthState() {
    // 应用启动时恢复认证状态
    try {
      console.log('尝试恢复认证状态...')
      await this.tryRefreshAuth()
      console.log('authStore.isValid:', this.client.authStore.isValid)
      console.log('authStore.record:', !!this.client.authStore.record)
      console.log('authStore.token:', !!this.client.authStore.token)

      // 检查 authStore 是否既有 token 又有 record（完整的认证状态）
      if (this.client.authStore.isValid && this.client.authStore.record && this.client.authStore.token) {
        const user = this.client.authStore.record
        const token = this.client.authStore.token
        
        console.log('从 authStore 恢复（完整状态）：', { 
          userId: user?.id, 
          email: user?.email,
          hasToken: !!token
        })
        
        const session = { token }
        this.notifyAuthStateChange(
          'INITIAL_SESSION',
          user,
          session
        )
      } else {
        console.log('authStore 状态不完整（isValid=' + this.client.authStore.isValid + ', hasRecord=' + !!this.client.authStore.record + ', hasToken=' + !!this.client.authStore.token + '），尝试从 localStorage 手动恢复...')
        // 如果 authStore 状态不完整，尝试从 localStorage 手动恢复认证数据
        const storedAuth = this.parseStoredAuthData()
        if (storedAuth && storedAuth.token && storedAuth.model) {
          console.log('从 localStorage 找到认证数据，准备恢复...')
          console.log('storedAuth.model 内容:', {
            id: storedAuth.model?.id,
            email: storedAuth.model?.email,
            name: storedAuth.model?.name
          })
          
          // 直接通知应用使用从 localStorage 解析的数据
          const session = { token: storedAuth.token }
          this.notifyAuthStateChange(
            'INITIAL_SESSION',
            storedAuth.model,
            session
          )
          
          console.log('✅ 会话已从 localStorage 恢复')
        } else {
          console.log('❌ 未找到有效的认证数据')
        }
      }
    } catch (error) {
      console.error('恢复认证状态失败:', error)
    }
  }

  parseStoredAuthData() {
    // 从 localStorage 中解析存储的认证数据
    try {
      if (typeof localStorage === 'undefined') {
        return null
      }

      // PocketBase 使用 'pocketbase_auth' 作为 localStorage key
      const storedData = localStorage.getItem('pocketbase_auth')
      if (!storedData) {
        console.log('localStorage 中未找到 pocketbase_auth')
        return null
      }

      console.log('找到 pocketbase_auth，尝试解析...')
      const parsed = JSON.parse(storedData)
      
      // 验证结构
      if (parsed && parsed.token && parsed.model) {
        console.log('✅ 成功解析 pocketbase_auth:', {
          hasToken: !!parsed.token,
          userId: parsed.model?.id,
          email: parsed.model?.email
        })
        return parsed
      } else {
        console.log('❌ pocketbase_auth 结构不完整:', {
          hasToken: !!parsed?.token,
          hasModel: !!parsed?.model
        })
        return null
      }
    } catch (error) {
      console.error('解析 localStorage 认证数据失败:', error)
      return null
    }
  }

  initializeAuthStateListener() {
    this.client.authStore.onChange(() => {
      const event = this.client.authStore.isValid ? 'SIGNED_IN' : 'SIGNED_OUT'
      const user = this.client.authStore.record
      const session = this.client.authStore.token ? { token: this.client.authStore.token } : null

      this.notifyAuthStateChange(event, user, session)
    })
  }

  notifyAuthStateChange(event, user, session) {
    const normalizedUser = user ? this.normalizeUser(user) : null
    const normalizedSession = session
      ? { ...session, user: normalizedUser }
      : null

    this.authStateCallbacks.forEach(callback => {
      callback(event, normalizedUser, normalizedSession)
    })
  }

  normalizeUser(user) {
    if (!user) {
      return null
    }

    return {
      ...user,
      id: user.id,
      user_metadata: {
        username: user.name || (user.email ? user.email.split('@')[0] : '')
      }
    }
  }

  async signUp(email, password, metadata = {}) {
    try {
      // 验证输入
      if (!email || !email.trim()) {
        throw new Error('邮箱不能为空')
      }
      if (!password || password.length < 6) {
        throw new Error('密码长度至少为 6 个字符')
      }

      // 准备用户名（PocketBase users 表使用 name 字段）
      let name = metadata.username || email.split('@')[0]
      name = (name || '').trim().substring(0, 50)

      if (!name) {
        throw new Error('用户名不能为空')
      }

      // 构建数据，使用 PocketBase users 表的实际字段
      const data = {
        email: email.trim().toLowerCase(),
        password,
        passwordConfirm: password,
        name  // PocketBase 内置 users 表的字段
      }

      const user = await this.client.collection('users').create(data)

      return { data: { user: this.normalizeUser(user) }, error: null }
    } catch (error) {
      // 提取 PocketBase 返回的错误信息
      const errorData = error.data || {}
      const message = errorData.message || error.message || '注册失败'
      
      console.error('PocketBase 注册错误:', {
        status: error.status,
        message: message,
        fieldErrors: errorData.data
      })

      return { data: null, error: error }
    }
  }

  async signIn(email, password) {
    try {
      // 验证输入
      if (!email || !email.trim()) {
        throw new Error('邮箱不能为空')
      }
      if (!password) {
        throw new Error('密码不能为空')
      }

      const authData = await this.client
        .collection('users')
        .authWithPassword(email.trim().toLowerCase(), password)

      const user = authData.record
      const session = { token: authData.token }

      return {
        data: { user: this.normalizeUser(user), session },
        error: null
      }
    } catch (error) {
      // 提取 PocketBase 返回的错误信息
      const errorData = error.data || {}
      const message = errorData.message || error.message || '登录失败'
      
      console.error('PocketBase 登录错误:', {
        status: error.status,
        message: message
      })

      return { data: null, error: error }
    }
  }

  async signOut() {
    try {
      this.client.authStore.clear()
      this.notifyAuthStateChange('SIGNED_OUT', null, null)
      return { error: null }
    } catch (error) {
      return { error: error }
    }
  }

  async getSession() {
    try {
      await this.ensureAuthReady()
      // 调试：检查 authStore 状态
      console.log('getSession() 调用 - authStore 状态:', {
        isValid: this.client.authStore.isValid,
        hasRecord: !!this.client.authStore.record,
        hasToken: !!this.client.authStore.token,
        record: this.client.authStore.record ? {
          id: this.client.authStore.record.id,
          email: this.client.authStore.record.email
        } : null
      })

      // 首先检查 authStore 中是否有有效的认证
      if (this.client.authStore.isValid) {
        const user = this.client.authStore.record
        const token = this.client.authStore.token

        if (user && token) {
          const session = { token }

          console.log('✅ 会话恢复成功（从 authStore）:', {
            userId: user.id,
            email: user.email,
            hasToken: !!token
          })

          return {
            data: { session: { user: this.normalizeUser(user), ...session } },
            error: null
          }
        }
      }

      // 如果 authStore 无效或数据不完整，尝试从 localStorage 恢复
      console.log('authStore 无效或数据不完整，尝试从 localStorage 恢复...')
      console.log('localStorage pocketbase_auth:', localStorage.getItem('pocketbase_auth'))
      
      const storedAuth = this.parseStoredAuthData()
      if (storedAuth && storedAuth.token && storedAuth.model) {
        console.log('✅ 会话从 localStorage 恢复成功:', {
          userId: storedAuth.model.id,
          email: storedAuth.model.email,
          hasToken: !!storedAuth.token
        })

        const session = { token: storedAuth.token }
        return {
          data: { session: { user: this.normalizeUser(storedAuth.model), ...session } },
          error: null
        }
      }

      console.log('❌ 无法恢复有效会话')
      return { data: { session: null }, error: null }
    } catch (error) {
      console.error('❌ 获取会话出错:', error)
      return { data: { session: null }, error: null }
    }
  }

  async getUser() {
    try {
      await this.ensureAuthReady()
      if (this.client.authStore.isValid && this.client.authStore.record) {
        const user = this.client.authStore.record
        return { data: { user: this.normalizeUser(user) }, error: null }
      }

      // 如果 authStore 无效，尝试从 localStorage 恢复
      const storedAuth = this.parseStoredAuthData()
      if (storedAuth && storedAuth.model) {
        return { data: { user: this.normalizeUser(storedAuth.model) }, error: null }
      }

      return { data: { user: null }, error: null }
    } catch (error) {
      return { data: { user: null }, error: error }
    }
  }

  onAuthStateChange(callback) {
    this.authStateCallbacks.push(callback)

    return {
      unsubscribe: () => {
        this.authStateCallbacks = this.authStateCallbacks.filter(
          cb => cb !== callback
        )
      }
    }
  }

  async ensureUserProfile(user, username) {
    try {
      if (!user || !user.id) {
        return
      }

      await this.client.collection('users').getOne(user.id)
    } catch (error) {
      // 用户不存在，PocketBase 中的 users 是内置集合，无需手动创建
      console.warn('User profile not found:', error)
    }
  }

  from(collection) {
    return new PocketBaseQueryBuilder(this, this.client, collection)
  }

  getDatabase() {
    return this.client
  }
}

/**
 * PocketBase 查询构建器
 */
class PocketBaseQueryBuilder extends QueryBuilder {
  constructor(provider, client, collection) {
    super()
    this.provider = provider
    this.client = client
    this.collection = collection
    this.filters = []
    this.sortBy = []
    this.selectedFields = null
  }

  mapAttribute(attribute) {
    switch (attribute) {
      case 'created_at':
        return 'created'
      case 'updated_at':
        return 'updated'
      default:
        return attribute
    }
  }

  select(fields = '*') {
    this.selectedFields = fields
    return this
  }

  async insert(data) {
    try {
      await this.provider.ensureAuthReady()
      const result = await this.client
        .collection(this.collection)
        .create(data)

      return { data: this.transformDocument(result), error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  update(data) {
    this.updateData = data

    const proxy = {
      eq: (column, value) => {
        this.eq(column, value)
        return proxy
      },
      in: (column, values) => {
        this.in(column, values)
        return proxy
      },
      updateExec: () => this.updateExec(),
      then: resolve => this.updateExec().then(resolve)
    }

    Object.setPrototypeOf(proxy, this)

    return proxy
  }

  async delete() {
    try {
      const docs = await this.exec()

      if (!docs.data || docs.data.length === 0) {
        return { error: null }
      }

      for (const doc of docs.data) {
        await this.client.collection(this.collection).delete(doc.id)
      }

      return { error: null }
    } catch (error) {
      return { error: error }
    }
  }

  eq(column, value) {
    const filterStr = this.buildFilterString(column, value)
    this.filters.push(filterStr)
    return this
  }

  in(column, values) {
    if (Array.isArray(values) && values.length > 0) {
      const conditions = values.map(v => `${column} = ${this.escapeValue(v)}`)
      const filterStr = conditions.join(' || ')
      this.filters.push(`(${filterStr})`)
    }
    return this
  }

  buildFilterString(column, value) {
    const escapedValue = this.escapeValue(value)
    return `${column} = ${escapedValue}`
  }

  escapeValue(value) {
    if (value === null || value === undefined) {
      return 'null'
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value)
    }
    if (typeof value === 'string') {
      return `"${value.replace(/"/g, '\\"')}"`
    }
    return `"${String(value)}"`
  }

  order(column, options = {}) {
    const mappedColumn = this.mapAttribute(column)
    const direction = options.ascending !== false ? '+' : '-'
    this.sortBy.push(`${direction}${mappedColumn}`)
    return this
  }

  transformDocument(doc) {
    if (!doc) return doc

    const transformed = { ...doc }

    // PocketBase 默认字段映射到标准字段名称
    if (transformed.created) {
      transformed.created_at = transformed.created
      delete transformed.created
    }

    if (transformed.updated) {
      transformed.updated_at = transformed.updated
      delete transformed.updated
    }

    return transformed
  }

  async single() {
    try {
      const result = await this.exec()
      if (result.data && result.data.length > 0) {
        return { data: result.data[0], error: null }
      }
      return { data: null, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  async exec() {
    try {
      await this.provider.ensureAuthReady()
      const filter = this.filters.length > 0 ? this.filters.join(' && ') : ''
      const sort = this.sortBy.length > 0 ? this.sortBy.join(',') : ''

      const query = this.client.collection(this.collection)

      const options = {
        filter,
        sort
      }

      if (this.selectedFields && this.selectedFields !== '*') {
        options.fields = this.selectedFields
      }

      const records = await query.getList(1, 500, options)
      const documents = records.items || []

      return {
        data: documents.map(doc => this.transformDocument(doc)),
        error: null
      }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  async updateExec() {
    try {
      const docs = await this.exec()

      if (!docs.data || docs.data.length === 0) {
        return { data: [], error: null }
      }

      const results = []
      for (const doc of docs.data) {
        const result = await this.client
          .collection(this.collection)
          .update(doc.id, this.updateData)

        results.push(this.transformDocument(result))
      }

      return { data: results, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }
}

export const pocketbaseConfig = {
  url: process.env.POCKETBASE_URL
}
